/* eslint-disable no-param-reassign */
import { createSlice } from '@reduxjs/toolkit';
import type { PayloadAction } from '@reduxjs/toolkit';
import { Mutex } from 'async-mutex';
import { createSelector } from 'reselect';
import { LOGOUT, LOGIN_SUCCESS, LOGOUT_START, LOGIN_START } from '@comeon/actions-public';
import { selectFranchiseCode } from '~/components/brand/ducks/selectors';
import { RootState, ThunkAction } from '~/store/types';
import { ACTION_PREFIX } from '../sportsbook-constants';
import selectSportsbookState from '../sportsbook-state-selector';
import { refresh as refreshBettingAuthentication, setUpBettingServiceApi } from './betting-service-api';
import {
	connectToSBApiWebsocketClient,
	disconnectFromSBApiWebsocketClient,
	setUpSportsbookApi
} from './sportsbook-api';
import reportSportsbookError from '../utils/sportsbook-error-reporter';

export { reconnectToSBApiWebsocketClientAndKeepSubscriptions } from './sportsbook-api';

const UUID = crypto && crypto.randomUUID ? crypto.randomUUID() : '0';

type State = {
	// SBAPI
	isSBApiInitialized: boolean;
	isSBApiLoading: boolean;
	hasSBApiError: boolean;
	// BETTING SERVICE
	isBettingServiceApiInitialized: boolean;
	hasBettingServiceApiError: boolean;
	isBettingServiceApiLoading: boolean;
	bettingSessionExpiresOn: string | null;
	bettingServiceApiRefCount: number;

	// GENERAL AUTHENTICATION
	isMobileviewAuthenticated: boolean;
};

type SetupAndConnectToSBApiParams = {
	franchiseCode: string;
	locale: string;
	localeCode: string;
	onSBApiWebsocketConnected?: () => void;
	onSBApiWebsocketConnectionError?: (reconnectAttempt: number) => void;
};

type SetupAndConnectToBettingServiceParams = {
	franchiseCode: string;
	locale: string;
	localeCode: string;
};

const initialState: State = {
	// SBAPI
	isSBApiInitialized: false,
	isSBApiLoading: false,
	hasSBApiError: false,
	// BETTING SERVICE
	isBettingServiceApiInitialized: false,
	isBettingServiceApiLoading: false,
	hasBettingServiceApiError: false,
	bettingSessionExpiresOn: null,
	bettingServiceApiRefCount: 0,
	// GENERAL AUTHENTICATION
	isMobileviewAuthenticated: false
};

const BETTING_SERVICE_LOADING = `${ACTION_PREFIX}API/BETTING_SERVICE_LOADING`;

const selectState = (state: RootState) => selectSportsbookState(state).api;

/* *** SBAPI selectors *** */
/**
 * Check if our SBAPI client is initialized. This does not mean that the client is ready to be used, only that the tasks to initialize the client has finished.
 */
export const selectIsSBApiInitialized = (state: RootState) => selectState(state).isSBApiInitialized;
/**
 * If there was an error in setting up or connecting to the SBAPI client we will save it to this state.
 */
export const selectHasSBApiError = (state: RootState) => selectState(state).hasSBApiError;
/**
 * Loading state for setting up and connecting to the SBAPI client.
 */
export const selectIsSBApiLoading = (state: RootState) => selectState(state).isSBApiLoading;
/**
 * Check if the SBAPI client is ready to be used - this means that the client has been setup and the websocket connection has been established succesfully.
 */
export const selectIsSBApiReady = createSelector(
	[selectIsSBApiInitialized, selectHasSBApiError, selectIsSBApiLoading],
	(isSBApiInitialized, hasSBApiError, isSBApiLoading) => isSBApiInitialized && !hasSBApiError && !isSBApiLoading
);

/* *** BETS & BETTING SERVICE SELECTORS *** */
/**
 * Check if the betting service is initialized. This does not mean that the client is ready to be used, only that the tasks to initialize the client has finished.
 */
export const selectIsBettingServiceApiInitialized = (state: RootState) =>
	selectState(state).isBettingServiceApiInitialized;
/**
 * If there was an error in setting up or connecting to the betting service client we will save it to this state.
 */
export const selectHasBettingServiceApiError = (state: RootState) => selectState(state).hasBettingServiceApiError;
/**
 * Loading state for setting up and connecting to the betting service client.
 */
export const selectIsBettingServiceApiLoading = (state: RootState) => selectState(state).isBettingServiceApiLoading;
/**
 * Check if the betting service api is ready to be used (refreshed for the correct app-authentication state)
 */
export const selectIsBettingServiceApiReady = createSelector(
	[selectIsBettingServiceApiInitialized, selectHasBettingServiceApiError],
	(isBettingServiceInitialized, hasBettingServiceApiError) =>
		isBettingServiceInitialized && !hasBettingServiceApiError
);
export const selectBettingSessionExpiresOn = (state: RootState) => selectState(state).bettingSessionExpiresOn;

export const selectBettingServiceApiRefCount = (state: RootState) => selectState(state).bettingServiceApiRefCount;

/* *** GENERAL SELECTORS */
/**
 * Combined selector to check if required modules has been successfully setup and connected. This is used to determine if we can display the Sportsbook lobby.
 */
export const selectHasSportsbookApiSetupFailed = createSelector([selectHasSBApiError], hasSBApiError => hasSBApiError);

// reducer
// SBAPI
/**
 * When the Sportsbook API client has been setup and connected succesfully we set the SBAPI module to a ready state
 */
const handleSBApiReady = (state: State) => {
	state.isSBApiInitialized = true;
	state.isSBApiLoading = false;
	state.hasSBApiError = false;
};

/**
 * During setup we set the SBAPI module to a loading state
 */
const handleSBApiLoading = (state: State) => {
	state.isSBApiLoading = true;
};
/**
 * An error while setting up or connecting to the Sportsbook API client will set the SBAPI module to a failed state
 */
const handleSBApiFailed = (state: State) => {
	state.isSBApiInitialized = true;
	state.isSBApiLoading = false;
	state.hasSBApiError = true;
};
/**
 * Reset the state of the SBAPI module
 */
const handleSBApiDisposed = (state: State) => {
	state.isSBApiInitialized = false;
	state.isSBApiLoading = false;
	state.hasSBApiError = false;
};

// BETTING SERVICE
const handleBettingServiceApiReady = (state: State, action: PayloadAction<{ bettingSessionExpiresOn: string }>) => {
	state.isBettingServiceApiInitialized = true;
	state.isBettingServiceApiLoading = false;
	state.hasBettingServiceApiError = false;
	state.bettingSessionExpiresOn = action.payload.bettingSessionExpiresOn;
};

const handleBettingServiceApiLoading = (state: State) => {
	state.isBettingServiceApiLoading = true;
};

const handleBettingServiceApiDisposed = (state: State) => {
	state.bettingServiceApiRefCount -= 1;
	if (state.bettingServiceApiRefCount <= 0) {
		state.isBettingServiceApiInitialized = false;
		state.isBettingServiceApiLoading = false;
		state.hasBettingServiceApiError = false;
		state.bettingSessionExpiresOn = null;
	}
};

const handleBettingServiceApiFailed = (state: State) => {
	state.isBettingServiceApiInitialized = true;
	state.isBettingServiceApiLoading = false;
	state.hasBettingServiceApiError = true;
};

const handleBettingServiceApiRefCount = (state: State) => {
	state.bettingServiceApiRefCount += 1;
};

// GENERAL AUTHENTICATION
const handleLogout = (state: State) => {
	state.isBettingServiceApiInitialized = false;
	state.isMobileviewAuthenticated = false;
};

const handleLogin = (state: State) => {
	state.isBettingServiceApiInitialized = false;
	state.isMobileviewAuthenticated = false;
};

const handleIsMobileviewAuthenticated = (state: State) => {
	state.isMobileviewAuthenticated = true;
};

export const apiSlice = createSlice({
	name: 'sportsbook-api',
	initialState,
	reducers: {
		// SBAPI
		sbApiReady: handleSBApiReady,
		sbApiLoading: handleSBApiLoading,
		sbApiFailed: handleSBApiFailed,
		sbApiDisposed: handleSBApiDisposed,
		// BETTING SERVICE
		bettingServiceApiReady: handleBettingServiceApiReady,
		bettingServiceApiLoading: handleBettingServiceApiLoading,
		bettingServiceApiDisposed: handleBettingServiceApiDisposed,
		bettingServiceApiFailed: handleBettingServiceApiFailed,
		bettingServiceApiRefCount: handleBettingServiceApiRefCount,
		// GENERAL AUTHENTICATION - Actions are global so we will reuse the shared constants
		[LOGOUT_START]: handleIsMobileviewAuthenticated,
		[LOGIN_START]: handleIsMobileviewAuthenticated,
		[LOGOUT]: handleLogout,
		[LOGIN_SUCCESS]: handleLogin
	}
});

const {
	sbApiReady,
	sbApiLoading,
	sbApiFailed,
	sbApiDisposed,
	bettingServiceApiDisposed,
	bettingServiceApiFailed,
	bettingServiceApiReady,
	bettingServiceApiRefCount
} = apiSlice.actions;

// Mutex to ensure we only auth once
const bettingClientMutex = new Mutex();
let refreshTimerId: NodeJS.Timeout | null = null;

export const refreshBettingServiceClient =
	(isBettingClientEnabled: boolean, forceRefresh?: boolean): ThunkAction =>
	async (dispatch, getState) => {
		return bettingClientMutex.runExclusive(async () => {
			dispatch({ type: BETTING_SERVICE_LOADING });
			const isReady = selectIsBettingServiceApiReady(getState());

			if (!isBettingClientEnabled && isReady) {
				dispatch(bettingServiceApiDisposed());
			} else if (isBettingClientEnabled && (!isReady || !!forceRefresh)) {
				if (refreshTimerId != null) {
					clearTimeout(refreshTimerId);
				}
				try {
					const franchiseCode = selectFranchiseCode(getState());

					const { bettingSessionExpiresOn } = await refreshBettingAuthentication(franchiseCode, UUID);
					dispatch(bettingServiceApiReady({ bettingSessionExpiresOn }));

					const now = new Date();
					if (!!bettingSessionExpiresOn && now < new Date(bettingSessionExpiresOn)) {
						const bettingSessionExpiresOnMs = new Date(bettingSessionExpiresOn).getTime() - now.getTime();
						refreshTimerId = setTimeout(() => {
							dispatch(refreshBettingServiceClient(true, true));
						}, bettingSessionExpiresOnMs);
					}
				} catch (error) {
					refreshTimerId = setTimeout(() => {
						dispatch(refreshBettingServiceClient(true, true));
					}, 5000);
					dispatch(bettingServiceApiFailed());
					reportSportsbookError('Error while refreshing Betting Service client', error);
				}
			}
		});
	};

/**
 * Setup and connect to the Sportsbook API client used by most features in the Sportsbook application.
 * It should be called once when the application starts and might get teared down when the application is closed and when a player logs out.
 */
export const setupAndConnectToSBApi =
	(params: SetupAndConnectToSBApiParams): ThunkAction =>
	async dispatch => {
		const { franchiseCode, locale, localeCode, onSBApiWebsocketConnected, onSBApiWebsocketConnectionError } =
			params;
		dispatch(sbApiLoading());

		try {
			setUpSportsbookApi({
				franchiseCode,
				locale,
				localeIsoCode: localeCode,
				onSBApiWebsocketConnected,
				onSBApiWebsocketConnectionError
			});
			await connectToSBApiWebsocketClient();
			dispatch(sbApiReady());
		} catch (error) {
			dispatch(sbApiFailed());
			reportSportsbookError('Error trying to setup to SBAPI client', error, { localeCode });
		}
	};

/**
 * Setup and connect to the Betting Service client used by the Sportsbook application.
 * It should be called once when the application starts and might get teared down when the application is closed and when a player logs out.
 */
export const setupAndConnectToBettingService =
	(params: SetupAndConnectToBettingServiceParams, isBettingClientEnabled: boolean): ThunkAction =>
	dispatch => {
		const { franchiseCode, locale, localeCode } = params;
		try {
			if (isBettingClientEnabled) {
				setUpBettingServiceApi({ franchiseCode, locale });
				dispatch(bettingServiceApiRefCount());
			}
		} catch (error) {
			dispatch(bettingServiceApiFailed());
			reportSportsbookError('Error trying to setup to BettingService client', error, { localeCode });
		}
	};

/**
 * Dispose and disconnect from Sportsbook API client
 */
export const disposeAndDisconnectFromSBApi = (): ThunkAction => dispatch => {
	dispatch(sbApiDisposed());
	disconnectFromSBApiWebsocketClient();
};

/**
 * Dispose and disconnect from the Betting Service client
 */
export const disposeAndDisconnectFromBettingService = () => bettingServiceApiDisposed();

export default apiSlice.reducer;
